import type { CameraTarget, MapEngine } from '@mighty-map/map-engine';
import type { SharedCamera, ViewMode } from '../../state/viewMode';

/**
 * The Map and Globe engines, registered by the views that own them, so the Map · BIM · Globe switch can read the
 * camera of the view it is leaving at the moment of the click (the view being entered reads it straight after).
 * BIM hands over its own camera.
 */
const engines: { map: MapEngine | null; globe: MapEngine | null } = { map: null, globe: null };

/** A tilt worth telling the viewer about when Map lands flat, and the least a Map's lean must be to hand it over. */
export const TILTED_DEGREES = 2;

/** The Globe's own tilt, kept while another view is on screen: a flat Map has none to give back. */
let globePitch = 0;
/** Whether the Globe has been on screen and left: from then on it keeps its own tilt. */
let globeOpened = false;
/** The view the current one was entered from. */
let enteredFrom: ViewMode = 'map';

export function registerViewEngine(view: 'map' | 'globe', engine: MapEngine | null): void {
  engines[view] = engine;
}

/** The camera of a Map or Globe view as the switch hands it over; null for BIM or a view not made yet. */
export function cameraOf(view: ViewMode): SharedCamera | null {
  const engine = view === 'map' ? engines.map : view === 'globe' ? engines.globe : null;
  if (!engine) return null;
  const c = engine.getCamera();
  return { center: [c.center[0], c.center[1]], zoom: c.zoom, bearing: c.bearing, pitch: c.pitch };
}

/** Record a switch: where it came from, and the Globe's tilt when it is the view being left. */
export function noteSwitch(from: ViewMode, camera: SharedCamera | null): void {
  enteredFrom = from;
  if (from === 'globe' && camera) {
    globePitch = camera.pitch;
    globeOpened = true;
  }
}

/** The camera the Globe opens with, from the camera handed over. From a tilted Map, the Map's own lean and turn (the
 *  2D map can lean too). From a flat Map: the tilt the Globe kept, once it has been open; before that, the theme's
 *  first Globe view when it gives one (`start`), else straight down. From anywhere else, the camera as it is. */
export function globeCameraFor(camera: SharedCamera, start?: { bearing?: number; pitch?: number }): SharedCamera {
  if (enteredFrom !== 'map' || camera.pitch > TILTED_DEGREES) return camera;
  if (!globeOpened && start) {
    return { ...camera, bearing: start.bearing ?? camera.bearing, pitch: start.pitch ?? 0 };
  }
  return { ...camera, pitch: globePitch };
}

/** Home on the map stays flat; on the globe it uses the theme's opening bearing and pitch when given. */
export function homeCameraFor(
  home: CameraTarget,
  view: ViewMode,
  globeStart?: { bearing?: number; pitch?: number },
): CameraTarget {
  if (view !== 'globe') return { bearing: 0, pitch: 0, ...home };
  return {
    ...home,
    bearing: globeStart?.bearing ?? home.bearing ?? 0,
    pitch: globeStart?.pitch ?? home.pitch ?? 0,
  };
}
